import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {useAxiosPrivate} from "../useAxiosPrivate";


export default function useReservationQueries() {
    const axiosPrivate = useAxiosPrivate();
    const queryClient = useQueryClient();

    // QUERIES ------------------------------------------------------

    const useReservationsUserQuery = useQuery({
        queryFn: () => axiosPrivate.get("admin-reservation/get_for_user/").then(r => r.data),
        queryKey: ["admin-reservation", "get_for_user"],
    });

    const reservationsBusinessQuery = useQuery({
        queryFn: () => axiosPrivate.get("admin-reservation/get_for_business/").then(r => r.data),
        queryKey: ["admin-reservation", "get_for_business"],
    });

    const useReservationDocumentQuery = (id) =>
        useQuery({
            queryKey: ["admin-reservation", "get_documents", id],
            queryFn: ({ queryKey }) => {
                const [, , reservationId] = queryKey;
                return axiosPrivate
                    .get(`admin-reservation/${reservationId}/get_documents/`)
                    .then((r) => r.data);
            },
            enabled: !!id, // prevents running until id exists
        });


    const useFreeSlotsForDate = (priceId, selectedDate) =>
        useQuery({
            queryKey: ["reservation", "get_free_slots_for_date", priceId, selectedDate],
            queryFn: ({ queryKey }) => {
                const [,, pid, date] = queryKey;
                return axiosPrivate
                    .get(`reservation/get_free_slots_for_date/?price=${pid}&date=${date}`)
                    .then((r) => r.data);
            },
            enabled: !!priceId && !!selectedDate, // prevents running until id exists
        });


    const useRemainingPlaces = (priceId, selectedDate, selectedStart) =>
        useQuery({
            queryKey: ["admin-reservation", "get_remaining_places", priceId, selectedDate, selectedStart],
            queryFn: ({ queryKey }) => {
                const [,, price_id, date_str, time_str] = queryKey;
                return axiosPrivate
                    .post(`admin-reservation/get_remaining_places/`, {price_id, date_str, time_str})
                    .then((r) => r.data);
            },
            enabled: !!priceId && !!selectedDate && !!selectedStart, // prevents running until id exists
        });


    const useMonthlyAvailability = (priceId, year, month) =>
        useQuery({
            queryKey: ["reservation", "monthly_availability", priceId, year, month],
            queryFn: ({ queryKey }) => {
                return axiosPrivate
                    .post(`reservation/monthly_availability/`, {year: year, month: month, price: priceId})
                    .then((r) => r.data);
            },
            enabled: !!priceId && !!year && !!month, // prevents running until id exists
        });


    // MUTATIONS ------------------------------------------------------

    const deleteReservationMutation = useMutation({
            mutationFn: id => axiosPrivate.delete(`admin-reservation/${id}`).then(r => r.data),
            onSuccess: (...args) => {
                queryClient.invalidateQueries(["admin-reservation"]);
            },
        });

    const acceptReservationMutation = useMutation({
            mutationFn: id => axiosPrivate.get(`admin-reservation/${id}/accept/`).then(r => r.data),
            onSuccess: (...args) => {
                queryClient.invalidateQueries(["admin-reservation"]);
            },
        });

    const rejectReservationMutation = useMutation({
            mutationFn: id => axiosPrivate.get(`admin-reservation/${id}/reject/`).then(r => r.data),
            onSuccess: (...args) => {
                queryClient.invalidateQueries(["admin-reservation"]);
            },
        });

    const createReservationMutation = useMutation({
            mutationFn: (data) => axiosPrivate.post(`admin-reservation/reserve_authenticated/`, data).then(r => r.data),
            onSuccess: (...args) => {
                queryClient.invalidateQueries(["admin-reservation"]);
            },
        });

    const createInvoiceMutation = useMutation({
            mutationFn: ({ id, data }) => axiosPrivate.post(`admin-reservation/${id}/create_invoice/`, data).then(r => r.data),
            onSuccess: (...args) => {
                queryClient.invalidateQueries(["admin-reservation"]);
            },
        });

    const updateReservationMutation = useMutation({
            mutationFn: ({ id, date, slot }) => axiosPrivate.post(`admin-reservation/${id}/update_date/`, { date, slot }).then(r => r.data),
            onSuccess: (...args) => {
                queryClient.invalidateQueries(["admin-reservation"]);
           },
        });

    const sendInvoiceMutation = useMutation({
            mutationFn: ({ id, doc_id }) => axiosPrivate.get(`admin-reservation/${id}/send_invoice/?doc_id=${doc_id}`).then(r => r.data),
        });

    const downloadInvoiceMutation = useMutation({
            mutationFn: ({ id, doc_id }) => axiosPrivate.get(`admin-reservation/${id}/download_invoice/?doc_id=${doc_id}`, { responseType: "blob" }).then(r => r.data),
        });

    return {
        useReservationsUserQuery,
        useReservationDocumentQuery,
        useRemainingPlaces,
        reservationsBusinessQuery,
        deleteReservationMutation,
        acceptReservationMutation,
        rejectReservationMutation,
        createInvoiceMutation,
        updateReservationMutation,
        sendInvoiceMutation,
        downloadInvoiceMutation,
        useFreeSlotsForDate,
        useMonthlyAvailability,
        createReservationMutation
    };
}